<template>
  <div class="InsuranceBodyMeasurementsForm">
    <h2 class="gl_form-title small">
      {{ t(`hiring.common.insurance-body-measurements-form.legend.${person}`) }}
    </h2>
    <section
      v-for="({ userDetails, isHolder, role }, index) in insureds"
      :key="index"
    >
      <UserCard
        :title="
          t('hiring.common.insurance-body-measurements-form.title-prefix', [userDetails.name]) +
          t(
            isHolder
              ? 'hiring.common.insurance-body-measurements-form.title-holder'
              : 'hiring.common.insurance-body-measurements-form.title-insured',
            { n: index + 1 }
          )
        "
      />

      <BaseInput
        :label="
          t(
            role === 'TA'
              ? 'hiring.common.insurance-body-measurements-form.weight-question.me'
              : 'hiring.common.insurance-body-measurements-form.weight-question.not-me'
          )
        "
        :id="`weight-${index}`"
        :placeholder="t('hiring.common.insurance-body-measurements-form.weight-label')"
        :model-value="userDetails.weight"
        @update:model-value="
          (value: string) => (insureds[index].userDetails.weight = getNumber(value))
        "
        :rules="[required, validationRules.validWeight, validationRules.maxValueKg]"
      />

      <BaseInput
        :label="
          t(
            role === 'TA'
              ? 'hiring.common.insurance-body-measurements-form.height-question.me'
              : 'hiring.common.insurance-body-measurements-form.height-question.not-me'
          )
        "
        :id="`height-${index}`"
        :placeholder="t('hiring.common.insurance-body-measurements-form.height-label')"
        :model-value="userDetails.height"
        @update:model-value="
          (value: string) => (insureds[index].userDetails.height = getNumber(value))
        "
        :rules="[required, validationRules.validHeight, validationRules.maxValueCm]"
      />
    </section>
  </div>
</template>

<script lang="ts" setup>
  import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { UserRoleEnum } from '@/modules/core/models/enums'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import UserCard from '@/modules/health/hiring/components/UserCardForm.vue'
  import { computed, onBeforeMount, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const { required, isPositiveDecimal, maxValueMeasure } = useRules()
  const loadingStore = useLoadingStore()

  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const insureds = ref(props.answers.insureds)

  const validationRules = {
    validWeight: (value: string) =>
      isPositiveDecimal(
        value,
        'hiring.common.insurance-body-measurements-form.weight-invalid-format'
      ),
    validHeight: (value: string) =>
      isPositiveDecimal(
        value,
        'hiring.common.insurance-body-measurements-form.height-invalid-format'
      ),
    maxValueKg: (value: string) => maxValueMeasure(value, 299, 'kg'),
    maxValueCm: (value: string) => maxValueMeasure(value, 299, 'cm')
  }

  function getNumber(value: string) {
    return (value.match(/\d{1,3}/g) ?? [''])[0]
  }

  const person = computed(() => {
    const number = props.answers.insureds.length
    const role = props.answers.insureds[0].role
    if (number === 1 && role === UserRoleEnum.HOLDER_INSURED) return 'me'
    else if (number === 1 && role === UserRoleEnum.INSURED) return 'not-me'
    else if (number > 1 && role === UserRoleEnum.HOLDER_INSURED) return 'holder'
    else return 'not-holder'
  })

  function triggerSubmit() {
    const page = props.nextForm[0]

    const answers = {
      insureds: insureds.value
    }

    return { page, answers }
  }

  function beforeMount() {
    setTimeout(loadingStore.stopLoading, 300)
  }

  onBeforeMount(beforeMount)
</script>

<style lang="scss" scoped>
  .InsuranceBodyMeasurementsForm {
    .UserCard {
      margin: $spacing-l 0;
    }
  }
</style>
